import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import DatePicker from "react-datepicker";
import { Link } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import { IoIosSearch } from "react-icons/io";
import { LuRefreshCw } from "react-icons/lu";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { FaUpload } from "react-icons/fa";
import LoadingSpinner from "../../components/LoadingSpinner";

const GeneralNotification = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState({
    title: "",
    message: "",
    screated_on: "",
    ecreated_on: "",
    orderbycol: "created_on",
    orderby: "DESC",
  });
  const [appliedFilters, setAppliedFilters] = useState({ ...filters });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    prevLink: "#",
    nextLink: "#",
    showingRecords: "Showing 0 to 0 of 0 entries",
    limit: 100,
  });

  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const toastShownRef = useRef(false);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      const params = {
        ...appliedFilters,
        page: pagination.currentPage,
        limit: pagination.limit,
      };

      const response = await axios.get(
        `${API_URL}PushNotification/GeneralNotification`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data && Array.isArray(response.data.data)) {
        setNotifications(response.data.data);

        const totalItems = response.data.total || 0;
        const totalPages = Math.ceil(totalItems / pagination.limit);

        setPagination((prev) => ({
          ...prev,
          totalItems,
          totalPages,
          showingRecords:
            response.data.showingRecords ||
            `Showing ${response.data.data.length} of ${totalItems} entries`,
        }));
      }

      if (!toastShownRef.current) {
        showToast.success("Notifications fetched successfully");
        toastShownRef.current = true;
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      showToast.error("Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    if (dates) {
      const [start, end] = dates;
      setFilters((prev) => ({
        ...prev,
        screated_on: start
          ? start.toLocaleDateString("en-GB").split("/").join("-")
          : "",
        ecreated_on: end
          ? end.toLocaleDateString("en-GB").split("/").join("-")
          : "",
      }));
    }
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    const defaultFilterState = {
      title: "",
      message: "",
      screated_on: "",
      ecreated_on: "",
      orderbycol: "created_on",
      orderby: "DESC",
    };
    setFilters(defaultFilterState);
    setAppliedFilters(defaultFilterState);
    setDateRange([null, null]);
  };

  const handleSort = (column) => {
    let sortOrder = "ASC";
    if (column === appliedFilters.orderbycol) {
      sortOrder = appliedFilters.orderby === "ASC" ? "DESC" : "ASC";
    }
    const newFilters = {
      ...appliedFilters,
      orderbycol: column,
      orderby: sortOrder,
    };
    setAppliedFilters(newFilters);
    setFilters(newFilters);
  };

  const handlePageChange = (direction) => {
    setPagination((prev) => {
      let newPage = prev.currentPage;
      if (direction === "prev" && prev.currentPage > 1) {
        newPage -= 1;
      } else if (direction === "next" && prev.currentPage < prev.totalPages) {
        newPage += 1;
      }
      return { ...prev, currentPage: newPage };
    });
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem("authToken");
      const exportParams = new URLSearchParams({
        ...appliedFilters,
        page: pagination.currentPage.toString(),
      });

      for (let [key, value] of exportParams.entries()) {
        if (!value || value === "") {
          exportParams.delete(key);
        }
      }

      const exportUrl = `${API_URL}PushNotification/GeneralNotification/export?${exportParams.toString()}`;
      const response = await fetch(exportUrl, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get("Content-Disposition");
        let filename = "general-notifications.xlsx";

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showToast.success("Export completed successfully");
      } else {
        throw new Error(`Export failed with status: ${response.status}`);
      }
    } catch (error) {
      console.error("Export error:", error);
      showToast.error("Failed to export notifications");
    } finally {
      setExportLoading(false);
    }
  };

  const getSortIcon = (column) => {
    if (appliedFilters.orderbycol !== column) return "sort";
    return appliedFilters.orderby === "ASC" ? "sort-down" : "sort-up";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [datePart, timePart] = dateString.split(" ");
    const [day, month, year] = datePart.split("-");
    return `${day}/${month}/${year} ${timePart}`;
  };
  // const formatDate = (dateString) => {
  //   if (!dateString) return "";
  //   const date = new Date(dateString);
  //   const day = String(date.getDate()).padStart(2, "0");
  //   const month = String(date.getMonth() + 1).padStart(2, "0");
  //   const year = date.getFullYear();

  //   // Extract time part in HH:MMam/pm format
  //   let hours = date.getHours();
  //   const minutes = String(date.getMinutes()).padStart(2, "0");
  //   const ampm = hours >= 12 ? "pm" : "am";
  //   hours = hours % 12 || 12;

  //   return `${day}-${month}-${year}, ${hours}:${minutes}${ampm}`;
  // };

  useEffect(() => {
    fetchNotifications();
  }, [appliedFilters, pagination.currentPage]);

  return (
    <div className="page-container mt-5">
      <div className="row pb-2">
        <div className="col-md-3"></div>
        <div className="col-md-9 d-flex justify-content-end align-items-center">
          <span className="me-3" style={{ fontSize: "0.8rem" }}>
            {pagination.showingRecords}
          </span>
          <button
            className="btn btn-default btn-sm me-2"
            onClick={() => fetchNotifications()}
          >
            <LuRefreshCw />
          </button>
          <button
            className="btn btn-default btn-sm me-2"
            onClick={handleExport}
            disabled={exportLoading}
          >
            {exportLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Exporting...
              </>
            ) : (
              <>
                <FaUpload /> Export
              </>
            )}
          </button>
          <button
            className="btn btn-default btn-sm me-2"
            onClick={() => handlePageChange("prev")}
            disabled={pagination.currentPage === 1}
            title="Previous"
          >
            <IoIosArrowBack />
          </button>
          <button
            className="btn btn-default btn-sm"
            onClick={() => handlePageChange("next")}
            disabled={pagination.currentPage === pagination.totalPages}
            title="Next"
          >
            <IoIosArrowForward />
          </button>
        </div>
      </div>

      <div className="panel panel-default with-rounded-corner with-shadow p-10">
        <div className="panel-heading">
          <span className="panel-title">General Notification</span>
        </div>

        <div className="table-responsive">
          <table className="table table-condensed table-hover bg-white">
            <thead>
              <tr>
                <th
                  width="30%"
                  onClick={() => handleSort("title")}
                  style={{ cursor: "pointer" }}
                >
                  Notification Title{" "}
                  <i className={`fa fa-${getSortIcon("title")}`}></i>
                </th>
                <th
                  width="45%"
                  onClick={() => handleSort("description")}
                  style={{ cursor: "pointer" }}
                >
                  Description{" "}
                  <i className={`fa fa-${getSortIcon("description")}`}></i>
                </th>
                <th
                  width="20%"
                  style={{ minWidth: "110px", cursor: "pointer" }}
                  onClick={() => handleSort("date_time")}
                >
                  Date Time{" "}
                  <i className={`fa fa-${getSortIcon("date_time")}`}></i>
                </th>
                <th width="5%">Action</th>
              </tr>
              <tr>
                <th>
                  <input
                    type="text"
                    name="title"
                    value={filters.title}
                    onChange={handleFilterChange}
                    className="form-control form-control-sm"
                    placeholder="Filter title"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    name="message"
                    value={filters.message}
                    onChange={handleFilterChange}
                    className="form-control form-control-sm"
                    placeholder="Filter message"
                  />
                </th>
                <th>
                  <div className="d-flex align-items-center">
                    <DatePicker
                      selectsRange
                      startDate={startDate}
                      endDate={endDate}
                      onChange={handleDateChange}
                      placeholderText="Filter date & time range"
                      className="form-control form-control-sm"
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="dd/MM/yyyy HH:mm"
                      monthsShown={2}
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      yearDropdownItemNumber={15}
                      scrollableYearDropdown
                      isClearable
                      popperPlacement="bottom-start"
                      popperClassName="custom-datepickerr"
                    />
                    <button
                      type="button"
                      className="btn btn-default btn-sm ms-2"
                      onClick={handleSearch}
                    >
                      <IoIosSearch />
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm ms-2"
                      onClick={handleClearFilters}
                    >
                      Clear
                    </button>
                  </div>
                </th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                // Skeleton loading rows
                Array.from({ length: 10 }).map((_, skeletonIndex) => (
                  <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                    {Array.from({ length: 4 }).map((_, colIndex) => (
                      <td key={colIndex}>
                        <div
                          className="skeleton-text"
                          style={{
                            width: "80%",
                            margin: "0 auto"
                          }}
                        ></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : notifications.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-3">
                    <p className="fw-bold">No Records Found</p>
                  </td>
                </tr>
              ) : (
                notifications.map((notification) => (
                  <tr key={notification.id}>
                    <td>{notification.title}</td>
                    <td style={{ whiteSpace: "pre-line" }}>
                      {notification.description}
                    </td>
                    <td>{formatDate(notification.date_time)}</td>
                    <td>
                      <Link
                        to={`/Campaign/GeneralNotification/view/${notification.id}`}
                        className="text-primary"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div id="myModal" className="modal">
        <div className="modal-dialog panel panel-default panel-border top">
          <div className="modal-content">
            <div id="myModalContent"></div>
          </div>
        </div>
      </div>

      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default GeneralNotification;
